import { SortOrder } from '@/enums/SortOrder';
import qs from 'qs';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';

const initialState: SectionTranslationState = {
  all: [],
  viewing: undefined,
};

const getters: GetterTree<SectionTranslationState, RootState> = {
  all(state: SectionTranslationState): SectionTranslation[] {
    return state.all;
  },
};

const mutations: MutationTree<SectionTranslationState> = {
  RESET(state: SectionTranslationState) {
    state.all = [];
  },
  ADD_MODEL(state: SectionTranslationState, payload: SectionTranslation) {
    state.all.push(payload);
  },
  ADD_MODELS(state: SectionTranslationState, payload: SectionTranslation[]) {
    state.all = [...state.all, ...payload];
  },
  SET_MODELS(state: SectionTranslationState, payload: SectionTranslation[]) {
    state.all = payload;
  },
  UPDATE_MODEL(state: SectionTranslationState, payload: SectionTranslation) {
    const index = state.all.findIndex((model: SectionTranslation) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      state.all[index] = payload;
    }
  },
  REMOVE_MODEL(state: SectionTranslationState, payload: number) {
    state.all = state.all.filter((model: SectionTranslation) => model.id !== payload);
  },
};

const resource: string = 'section-translations';

const actions: ActionTree<SectionTranslationState, RootState> = {
  clearViewing({ commit }) {
    commit('SET_VIEWING', undefined);
  },
  create({ commit, rootState }, { data, workspaceId }: { data: SectionTranslationData; workspaceId: number }) {
    return rootState.api
      .post(`${resource}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: SectionTranslation }) => {
        commit('ADD_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index({ commit, rootState }, { query = { s: { created_at: SortOrder.DESC, id: SortOrder.ASC }, per_page: 20 }, workspaceId }: { query?: IndexQuery<SectionTranslation>; workspaceId: number }) {
    const queryString = qs.stringify(query, { encode: false });

    return rootState.api
      .get(`${resource}?${queryString}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: SectionTranslation }) => {
        if (query.hasOwnProperty('after')) {
          commit('ADD_MODELS', response.data);
        } else {
          commit('SET_MODELS', response.data);
        }
        return Promise.resolve(response);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .get(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: SectionTranslation }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, { id, data, workspaceId }: { id: number; data: SectionTranslationData; workspaceId: number }) {
    return rootState.api
      .put(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: SectionTranslation }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  patch({ commit, rootState }, { id, data, workspaceId }: { id: number; data: Partial<SectionTranslationData>; workspaceId: number }) {
    return rootState.api
      .patch(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: SectionTranslation }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .delete(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: SectionTranslation }) => {
        commit('REMOVE_MODEL', id);

        return Promise.resolve(id);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  reset({ commit, rootState }) {
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
